// /admin/bonds/$id — Bond Inspector: read-only diagnostic view for a
// single bond. Surfaces the current LLM state + memory packet + the
// full conversation, so the operator can debug why an Ember sounded
// wrong or why a conversation drifted.

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  type AdminBondInspect,
  deleteAdminBond,
  getAdminBondInspect,
  unlockAdminEchoform,
} from '../lib/api';
import { useEmberTheme } from '../lib/use-ember-theme';

export const Route = createFileRoute('/admin/bonds/$id')({
  loader: async ({ params }) => {
    const data = await getAdminBondInspect(params.id);
    return { data };
  },
  component: BondInspectorPage,
});

function fmtAt(iso: string): string {
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ');
}

function BondInspectorPage() {
  const { data } = Route.useLoaderData();
  // Wear the inspected bond's colors while the page is mounted, so the
  // operator sees the same atmosphere the user does. Ephemeral so the
  // operator's own bond palette returns on unmount.
  useEmberTheme(data.bond.emberColorPalette, { persistent: false });
  return (
    <section className="space-y-10">
      <Link
        to="/admin/bonds"
        className="inline-block font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em] transition hover:text-ash-200"
      >
        ← Bonds
      </Link>

      <BondHeader data={data} />
      <StateBlock data={data} />
      <DnaBlock data={data} />
      {data.bondSoul && <SoulBlock soul={data.bondSoul} />}
      <MemoriesBlock memories={data.memories} />
      <ThreadBlock data={data} />
    </section>
  );
}

function BondHeader({ data }: { data: AdminBondInspect }) {
  const b = data.bond;
  return (
    <header className="border-paper-800/60 border-b pb-6">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-7">
        {/* Portrait. Square mask, modest size — the inspector is a
         * diagnostic surface so the image earns its place but doesn't
         * dominate. Falls back to a placeholder block when the bond's
         * portrait hasn't been generated yet. */}
        <figure className="relative flex-shrink-0">
          {b.emberImageUrl ? (
            <img
              src={b.emberImageUrl}
              alt={`${b.emberName} portrait`}
              className="ember-portrait-mask block h-32 w-32 select-none object-cover sm:h-36 sm:w-36 lg:h-40 lg:w-40"
              draggable={false}
            />
          ) : (
            <div className="flex h-32 w-32 items-center justify-center rounded-sm border border-paper-800/60 bg-paper-900/40 sm:h-36 sm:w-36 lg:h-40 lg:w-40">
              <span className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
                no portrait
              </span>
            </div>
          )}
        </figure>

        <div className="flex-1">
          <h2 className="font-display font-medium text-ash-50 text-3xl tracking-wide lg:text-4xl">
            {b.emberName}
          </h2>
          {b.emberSubtitle && (
            <p className="mt-1 font-body text-ash-300 italic lg:text-lg">{b.emberSubtitle}</p>
          )}

          {/* Identity row: element + echoes + relationship state pill.
           * The pieces the operator scans first when something feels
           * off. */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="rounded-sm border border-paper-800/60 bg-paper-900/40 px-3 py-1 font-display text-ash-100 text-xs uppercase tracking-[0.34em]">
              {b.primaryElement}
            </span>
            {b.secondaryEchoes.length > 0 && (
              <span className="font-display text-[0.66rem] text-ash-300 tracking-wide">
                echo of {b.secondaryEchoes.join(', ').toLowerCase()}
              </span>
            )}
            {b.relationshipState && (
              <span className="rounded-sm border border-accent/50 bg-paper-800/50 px-3 py-1 font-display text-accent-strong text-xs uppercase tracking-[0.34em]">
                {b.relationshipState}
              </span>
            )}
            {b.preUnstableState && (
              <span className="font-display text-[0.62rem] text-ash-400">
                (was {b.preUnstableState})
              </span>
            )}
          </div>

          {/* Context row: smaller, italic, body face. Started + email
           * + any other "metadata about the bond" not about its
           * identity. */}
          <p className="mt-3 font-body text-ash-500 text-sm italic">
            started {fmtAt(b.bondStartedAt)}
            {b.userEmail && (
              <>
                <span aria-hidden="true" className="mx-2 text-ash-700">
                  ·
                </span>
                {b.userEmail}
              </>
            )}
          </p>

          {/* Echoform manual unlock — MVP operator tool for triggering
           * Codex/Dream/Deep before auto-triggers exist. Memory has an
           * auto-trigger (1st memory_shard letter), but exposing it
           * here too keeps the button row consistent. */}
          <div className="mt-5">
            <UnlockEchoformAction
              bondId={b.id}
              unlockedEchoforms={data.bond.unlockedEchoforms ?? []}
            />
          </div>

          {/* Destructive action: delete the bond and everything attached
           * to it. Type-to-confirm inline so a misclick is harmless. */}
          <div className="mt-3">
            <DeleteBondAction bondId={b.id} emberName={b.emberName} />
          </div>
        </div>
      </div>
    </header>
  );
}

type EchoformKey = 'memory' | 'codex' | 'dream' | 'deep';

// localStorage key namespace for echoform pending state. We persist
// "what the operator has enqueued in this browser, for this bond,
// that hasn't shown up as unlocked yet" so the pending pill survives
// a page refresh. Cleared automatically when the server-side bond
// finally shows the form as unlocked (the job completed and the row
// was updated).
const ECHOFORM_PENDING_LS_KEY = (bondId: string) => `athrelaris.echoform-pending.v1.${bondId}`;

function readPendingFromStorage(bondId: string): EchoformKey | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ECHOFORM_PENDING_LS_KEY(bondId));
    if (raw === 'memory' || raw === 'codex' || raw === 'dream' || raw === 'deep') return raw;
    return null;
  } catch {
    return null;
  }
}

function writePendingToStorage(bondId: string, form: EchoformKey | null): void {
  if (typeof window === 'undefined') return;
  try {
    const key = ECHOFORM_PENDING_LS_KEY(bondId);
    if (form === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, form);
  } catch {
    // localStorage can be disabled (private mode); fail silently.
  }
}

function UnlockEchoformAction({
  bondId,
  unlockedEchoforms,
}: {
  bondId: string;
  unlockedEchoforms: string[];
}) {
  const unlocked = new Set(unlockedEchoforms);

  // Read the persisted pending value at mount. If the server-side bond
  // already shows the form as unlocked, the job completed and we can
  // clear the storage and not show pending anymore.
  const [enqueuedThisSession, setEnqueuedThisSession] = useState<EchoformKey | null>(() => {
    const stored = readPendingFromStorage(bondId);
    if (stored && !unlocked.has(stored)) return stored;
    if (stored && unlocked.has(stored)) writePendingToStorage(bondId, null);
    return null;
  });

  const [pendingConfirm, setPendingConfirm] = useState<EchoformKey | null>(null);
  const [busy, setBusy] = useState<EchoformKey | null>(null);
  const [message, setMessage] = useState<string | null>(
    enqueuedThisSession
      ? `${enqueuedThisSession}: still generating — refresh to check`
      : null,
  );

  async function trigger(echoForm: EchoformKey) {
    if (busy) return;
    setBusy(echoForm);
    setPendingConfirm(null);
    setMessage(null);
    try {
      const res = await unlockAdminEchoform(bondId, echoForm);
      if (res.alreadyUnlocked) {
        setMessage(`${echoForm}: already unlocked`);
      } else {
        setEnqueuedThisSession(echoForm);
        writePendingToStorage(bondId, echoForm);
        setMessage(`${echoForm}: enqueued — wait ~25s then refresh the page to see the portrait`);
      }
    } catch (err) {
      setMessage(`${echoForm}: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(null);
    }
  }

  function onClickSlot(form: EchoformKey) {
    if (unlocked.has(form)) return; // already unlocked — button is disabled
    if (busy || enqueuedThisSession) return;
    if (pendingConfirm === form) {
      // Second click confirms
      trigger(form);
    } else {
      // First click arms confirmation for this form
      setPendingConfirm(form);
      setMessage(null);
    }
  }

  // After a successful enqueue, ALL buttons stay disabled until the
  // page reloads. Prevents racing the in-flight gen with a second
  // enqueue (the job is idempotent at the storage-key level but the
  // operator shouldn't be invited to dispatch parallel gens for one
  // bond).
  const isLocked = enqueuedThisSession !== null;

  return (
    <div>
      <p className="mb-2 font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
        Unlock echoform (operator)
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {(['memory', 'codex', 'dream', 'deep'] as const).map((form) => {
          const isUnlocked = unlocked.has(form);
          const isPending = pendingConfirm === form;
          const isBusy = busy === form;
          const isEnqueuedHere = enqueuedThisSession === form;
          return (
            <button
              key={form}
              type="button"
              onClick={() => onClickSlot(form)}
              disabled={isUnlocked || busy !== null || isLocked}
              title={
                isUnlocked
                  ? `${form} already unlocked for this bond`
                  : isLocked && !isEnqueuedHere
                    ? 'another echoform is generating — refresh after it completes'
                    : undefined
              }
              className={`flex items-center gap-2 rounded-sm border px-3 py-1.5 font-display text-[0.62rem] uppercase tracking-[0.34em] transition disabled:cursor-not-allowed disabled:opacity-60 ${
                isPending
                  ? 'border-accent-strong bg-accent-strong/20 text-accent-strong hover:bg-accent-strong/30'
                  : isEnqueuedHere
                    ? 'border-accent/70 bg-paper-800/70 text-accent disabled:opacity-100'
                    : isUnlocked
                      ? 'border-ash-700 bg-paper-900/30 text-ash-600'
                      : 'border-accent/40 bg-paper-900/40 text-accent hover:bg-paper-800/60'
              }`}
            >
              {isEnqueuedHere && (
                <span
                  aria-hidden="true"
                  className="inline-block h-1.5 w-1.5 animate-pulseCore rounded-full bg-accent"
                />
              )}
              {isBusy
                ? '…'
                : isEnqueuedHere
                  ? `${form} · pending`
                  : isUnlocked
                    ? `${form} ✓`
                    : isPending
                      ? `confirm ${form}?`
                      : form}
            </button>
          );
        })}
        {pendingConfirm && !isLocked && (
          <button
            type="button"
            onClick={() => {
              setPendingConfirm(null);
              setMessage(null);
            }}
            disabled={busy !== null}
            className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.3em] underline decoration-ash-700 underline-offset-4 transition hover:text-ash-300"
          >
            cancel
          </button>
        )}
        {isLocked && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="font-display text-[0.6rem] text-accent uppercase tracking-[0.3em] underline decoration-accent/50 underline-offset-4 transition hover:text-accent-strong"
          >
            refresh page
          </button>
        )}
      </div>
      {pendingConfirm && !isLocked && (
        <p className="mt-2 font-body text-ash-400 text-xs italic">
          Click <strong className="not-italic text-accent-strong">{pendingConfirm}</strong> again to
          enqueue the gen ($0.08 to Fal). Cancel to abort.
        </p>
      )}
      {message && <p className="mt-2 font-body text-ash-400 text-xs italic">{message}</p>}
    </div>
  );
}

function DeleteBondAction({ bondId, emberName }: { bondId: string; emberName: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmed = typed === emberName;

  async function onDelete() {
    if (!confirmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAdminBond(bondId, typed);
      // Bounce out — the row this page rendered no longer exists.
      navigate({ to: '/admin/bonds' });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-sm border border-accent-strong/40 bg-paper-900/40 px-3 py-1.5 font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.34em] transition hover:bg-paper-800/60"
      >
        Delete bond…
      </button>
    );
  }

  return (
    <div className="rounded-sm border border-accent-strong/40 bg-paper-900/50 p-4">
      <p className="font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.34em]">
        Delete bond — destructive
      </p>
      <p className="mt-2 font-body text-ash-300 text-sm italic leading-relaxed">
        Removes the bond, every letter (Ember + user), all memories, the bond soul,
        the portrait file, and all cost telemetry rows for this bond. Cannot be
        undone. Type{' '}
        <code className="rounded-sm bg-paper-800/60 px-1.5 py-0.5 font-mono text-ash-100 text-xs">
          {emberName}
        </code>{' '}
        below to enable the delete.
      </p>
      <input
        type="text"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        disabled={busy}
        placeholder={emberName}
        className="mt-3 w-full max-w-sm rounded-sm border border-paper-800/60 bg-paper-950/60 px-3 py-1.5 font-body text-ash-100 text-sm placeholder:text-ash-600 focus:border-accent-strong/60 focus:outline-none"
        autoFocus
      />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onDelete}
          disabled={!confirmed || busy}
          className="rounded-sm border border-accent-strong bg-accent-strong/20 px-3 py-1.5 font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.34em] transition hover:bg-accent-strong/30 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? 'Deleting…' : 'Delete permanently'}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setTyped('');
            setError(null);
          }}
          disabled={busy}
          className="rounded-sm border border-paper-800/60 bg-paper-900/40 px-3 py-1.5 font-display text-[0.62rem] text-ash-300 uppercase tracking-[0.34em] transition hover:bg-paper-800/60 disabled:opacity-40"
        >
          Cancel
        </button>
        {error && (
          <span className="font-body text-accent-strong text-xs italic">{error}</span>
        )}
      </div>
    </div>
  );
}

function StateBlock({ data }: { data: AdminBondInspect }) {
  const b = data.bond;
  return (
    <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <SmallStat label="Letters" value={String(data.letters.length)} />
      <SmallStat label="User letters" value={String(data.userLetters.length)} />
      <SmallStat label="Memory shards" value={String(b.memoryShardCount)} />
      <SmallStat
        label="Total spent"
        value={data.cost.totalUsd != null ? `$${data.cost.totalUsd.toFixed(4)}` : '—'}
        sub={`${data.cost.events} events`}
      />

      <SentimentTimeline values={b.recentSentimentScores} />
      <OutcomeTimeline values={b.recentLetterOutcomes} />
    </section>
  );
}

function SmallStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-sm border border-paper-800/60 bg-paper-900/40 p-4">
      <p className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">{label}</p>
      <p className="mt-2 font-display text-ash-50 text-2xl">{value}</p>
      {sub && <p className="mt-1 font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.3em]">{sub}</p>}
    </div>
  );
}

// Sentiment scores 1-5: 1 = dismissive/cold, 3 = neutral, 5 = engaged/warm.
// Visualised as a horizontal sequence of large numerals coloured by
// value so an at-a-glance pattern (declining? oscillating?) reads
// without parsing labels. Oldest first → newest last.
function SentimentTimeline({ values }: { values: number[] }) {
  const toneFor = (v: number): string => {
    if (v <= 1) return 'border-accent-strong/60 bg-paper-800/60 text-accent-strong';
    if (v === 2) return 'border-accent/50 bg-paper-900/60 text-accent';
    if (v === 3) return 'border-paper-700 bg-paper-900/40 text-ash-200';
    if (v === 4) return 'border-paper-700 bg-paper-900/40 text-ash-100';
    return 'border-paper-700 bg-paper-900/40 text-ash-50';
  };
  return (
    <div className="rounded-sm border border-paper-800/60 bg-paper-900/40 p-4 sm:col-span-2">
      <p className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
        Recent sentiment <span className="text-ash-600">· 1 dismissive → 5 engaged</span>
      </p>
      {values.length === 0 ? (
        <p className="mt-3 font-body text-ash-400 text-sm">empty rolling window</p>
      ) : (
        <ol className="mt-3 flex flex-wrap items-center gap-2">
          {values.map((v, i) => (
            <li
              key={`${v}-${i}`}
              className={`flex h-9 w-9 items-center justify-center rounded-sm border font-display font-medium text-base tabular-nums sm:h-10 sm:w-10 sm:text-lg ${toneFor(
                v,
              )}`}
              title={`Sentiment ${v}/5`}
            >
              {v}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// Outcomes are categorical (positive / negative / neutral / dismissed
// / faded). Rendered as pills with semantic colouring so the
// trajectory of the bond reads at a glance: green-ish for positive,
// accent-strong for negative-zone, neutral grey otherwise.
function OutcomeTimeline({ values }: { values: string[] }) {
  const toneFor = (v: string): string => {
    if (v === 'negative' || v === 'dismissed') {
      return 'border-accent-strong/60 bg-paper-800/60 text-accent-strong';
    }
    if (v === 'positive') {
      return 'border-accent/50 bg-paper-900/60 text-ash-50';
    }
    if (v === 'faded') {
      return 'border-paper-800 bg-paper-950/60 text-ash-500';
    }
    return 'border-paper-700 bg-paper-900/40 text-ash-200';
  };
  return (
    <div className="rounded-sm border border-paper-800/60 bg-paper-900/40 p-4 sm:col-span-2">
      <p className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
        Recent outcomes
      </p>
      {values.length === 0 ? (
        <p className="mt-3 font-body text-ash-400 text-sm">empty rolling window</p>
      ) : (
        <ol className="mt-3 flex flex-wrap items-center gap-2">
          {values.map((v, i) => (
            <li
              key={`${v}-${i}`}
              className={`rounded-sm border px-2.5 py-1 font-display text-[0.65rem] uppercase tracking-[0.3em] ${toneFor(
                v,
              )}`}
            >
              {v}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function DnaBlock({ data }: { data: AdminBondInspect }) {
  const b = data.bond;
  const fields: { label: string; value: string }[] = [
    { label: 'Temperament', value: b.temperament },
    { label: 'First fear', value: b.firstFear },
    { label: 'First longing', value: b.firstLonging },
    { label: 'Broken memory seed', value: b.brokenMemorySeed },
    {
      label: 'First words from user',
      value: b.firstWordsFromUser ?? '— (pre-B3a bond, not captured)',
    },
  ];
  return (
    <section>
      <h3 className="mb-3 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
        Bond DNA
      </h3>
      <dl className="grid gap-3 sm:grid-cols-2">
        {fields.map((f) => (
          <div
            key={f.label}
            className="rounded-sm border border-paper-800/60 bg-paper-900/30 p-3"
          >
            <dt className="font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.3em]">
              {f.label}
            </dt>
            <dd className="mt-1 font-body text-ash-200 text-sm italic leading-relaxed">{f.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function SoulBlock({ soul }: { soul: NonNullable<AdminBondInspect['bondSoul']> }) {
  return (
    <section>
      <header className="mb-3 flex items-baseline gap-3">
        <h3 className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
          Bond soul
        </h3>
        <span className="font-display text-[0.58rem] text-ash-400">
          updated {fmtAt(soul.updatedAt)} · synthesised from {soul.sourceMemoryCount} memories
        </span>
      </header>
      <article className="markdown-soul rounded-sm border border-paper-800/60 bg-paper-900/30 p-5 lg:p-6">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <h1 className="mb-3 font-display font-medium text-ash-50 text-xl tracking-wide first:mt-0">
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 className="mt-5 mb-2 font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.34em] first:mt-0">
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 className="mt-4 mb-2 font-display text-[0.6rem] text-ash-400 uppercase tracking-[0.3em]">
                {children}
              </h3>
            ),
            p: ({ children }) => (
              <p className="mb-3 font-body text-ash-200 text-sm leading-relaxed last:mb-0 lg:text-[0.95rem]">
                {children}
              </p>
            ),
            ul: ({ children }) => (
              <ul className="mb-3 space-y-1 font-body text-ash-200 text-sm last:mb-0 lg:text-[0.95rem]">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="mb-3 list-decimal space-y-1 pl-5 font-body text-ash-200 text-sm last:mb-0 lg:text-[0.95rem]">
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li className="leading-relaxed before:mr-2 before:text-accent before:content-['◇']">
                {children}
              </li>
            ),
            em: ({ children }) => <em className="text-ash-100 italic">{children}</em>,
            strong: ({ children }) => (
              <strong className="font-medium text-ash-50">{children}</strong>
            ),
            code: ({ children }) => (
              <code className="rounded-sm bg-paper-800/60 px-1.5 py-0.5 font-mono text-ash-100 text-xs">
                {children}
              </code>
            ),
            blockquote: ({ children }) => (
              <blockquote className="my-3 border-accent/40 border-l-2 pl-4 font-body text-ash-300 italic">
                {children}
              </blockquote>
            ),
            hr: () => <hr className="my-4 border-paper-800/60" />,
          }}
        >
          {soul.contentMd}
        </ReactMarkdown>
      </article>
    </section>
  );
}

function MemoriesBlock({ memories }: { memories: AdminBondInspect['memories'] }) {
  if (memories.length === 0) {
    return (
      <section>
        <h3 className="mb-3 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
          Active memories
        </h3>
        <p className="font-body text-ash-300 text-sm">
          No memories yet — the bond is fresh, or the memory pipeline hasn't promoted any
          candidates.
        </p>
      </section>
    );
  }
  const grouped = new Map<string, AdminBondInspect['memories']>();
  for (const m of memories) {
    const arr = grouped.get(m.category) ?? [];
    arr.push(m);
    grouped.set(m.category, arr);
  }
  return (
    <section>
      <h3 className="mb-3 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
        Active memories
      </h3>
      <div className="space-y-5">
        {[...grouped.entries()].map(([category, items]) => (
          <div key={category}>
            <p
              className={`mb-2 font-display text-[0.6rem] uppercase tracking-[0.34em] ${
                category === 'boundary' ? 'text-accent-strong' : 'text-ash-400'
              }`}
            >
              {category} · {items.length}
            </p>
            <ul className="space-y-1.5">
              {items.map((m) => (
                <li
                  key={m.id}
                  className={`rounded-sm border p-3 ${
                    category === 'boundary'
                      ? 'border-accent-strong/30 bg-paper-900/40'
                      : 'border-paper-800/50 bg-paper-900/25'
                  }`}
                >
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.3em]">
                      importance {m.importance}
                    </span>
                    {m.lastReferencedAt && (
                      <span className="font-display text-[0.58rem] text-ash-400">
                        last referenced {fmtAt(m.lastReferencedAt)}
                      </span>
                    )}
                    <span className="ml-auto font-display text-[0.56rem] text-ash-600 italic">
                      {fmtAt(m.extractedAt)}
                    </span>
                  </div>
                  <p className="mt-1.5 font-body text-ash-200 text-sm italic leading-relaxed">
                    {m.content}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

interface ThreadEntry {
  kind: 'ember' | 'user';
  at: Date;
  letter?: AdminBondInspect['letters'][number];
  userLetter?: AdminBondInspect['userLetters'][number];
}

function ThreadBlock({ data }: { data: AdminBondInspect }) {
  const entries: ThreadEntry[] = [];
  for (const l of data.letters) entries.push({ kind: 'ember', at: new Date(l.generatedAt), letter: l });
  for (const u of data.userLetters)
    entries.push({ kind: 'user', at: new Date(u.receivedAt), userLetter: u });
  entries.sort((a, b) => a.at.getTime() - b.at.getTime());

  if (entries.length === 0) {
    return (
      <section>
        <h3 className="mb-3 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
          Thread
        </h3>
        <p className="font-body text-ash-300 text-sm">
          No letters yet (bond likely still in the post-Awakening generation window).
        </p>
      </section>
    );
  }

  return (
    <section>
      <h3 className="mb-3 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
        Thread · {entries.length} entries
      </h3>
      <ul className="space-y-3">
        {entries.map((e, idx) =>
          e.kind === 'ember' ? (
            <EmberEntryRow key={`l-${e.letter?.id ?? idx}`} l={e.letter as AdminBondInspect['letters'][number]} />
          ) : (
            <UserEntryRow key={`u-${e.userLetter?.id ?? idx}`} u={e.userLetter as AdminBondInspect['userLetters'][number]} />
          ),
        )}
      </ul>
    </section>
  );
}

function EmberEntryRow({ l }: { l: AdminBondInspect['letters'][number] }) {
  const isFallback = l.safetyFlags.includes('fallback');
  return (
    <li
      className={`rounded-sm border p-4 ${
        isFallback
          ? 'border-accent-strong/40 bg-paper-900/40'
          : 'border-paper-800/50 bg-paper-900/25'
      }`}
    >
      <div className="mb-2 flex flex-wrap items-baseline gap-3 font-display text-[0.58rem] uppercase tracking-[0.3em]">
        <span className="text-accent-strong">EMBER · {l.letterType}</span>
        {l.pendingChoice && <span className="text-accent">◇ choice</span>}
        {l.retryCount > 0 && (
          <span className="text-ash-500">{l.retryCount} retr{l.retryCount === 1 ? 'y' : 'ies'}</span>
        )}
        {isFallback && <span className="text-accent-strong">fallback</span>}
        {!l.readAt && <span className="text-ash-500">unread</span>}
        <span className="ml-auto text-ash-400 normal-case">{fmtAt(l.generatedAt)}</span>
      </div>
      <p
        className={`whitespace-pre-line font-body text-sm leading-relaxed ${
          isFallback ? 'text-ash-300 italic' : 'text-ash-100'
        }`}
      >
        {l.body}
      </p>
      {l.pendingChoice && (
        <p className="mt-2 font-body text-ash-400 text-xs italic">
          options: {l.pendingChoice.options.join(' / ')}
        </p>
      )}
    </li>
  );
}

function UserEntryRow({ u }: { u: AdminBondInspect['userLetters'][number] }) {
  return (
    <li className="rounded-sm border border-paper-800/40 bg-paper-950/30 p-4">
      <div className="mb-2 flex flex-wrap items-baseline gap-3 font-display text-[0.58rem] uppercase tracking-[0.3em]">
        <span className="text-ash-300">USER</span>
        {u.sentimentScore != null && (
          <span className="text-ash-500">sentiment {u.sentimentScore}</span>
        )}
        {u.sentimentTags.length > 0 && (
          <span className="text-ash-500">{u.sentimentTags.join(', ')}</span>
        )}
        {u.choiceResolution && (
          <span className="text-accent">resolved: {u.choiceResolution}</span>
        )}
        {u.safetyFlags.length > 0 && (
          <span className="text-accent-strong">flags: {u.safetyFlags.join(', ')}</span>
        )}
        <span className="ml-auto text-ash-400 normal-case">{fmtAt(u.receivedAt)}</span>
      </div>
      <p className="whitespace-pre-line font-body text-ash-300 text-sm italic leading-relaxed">
        {u.body || '(empty)'}
      </p>
    </li>
  );
}
